import React from "react";
import type { ApolloClient } from "@apollo/client";
import {
  createAndLinkSecondaryAuth,
  createAuthLinkingSession,
  linkSecondaryAuth,
} from "actions/userActions";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Auth0Context } from "utils/react-auth0-wrapper";
import IconSpinner from "components/IconSpinner";
import { FcGoogle } from "react-icons/fc";
import { get } from "lodash";

import Button from "../../components/Button";
import IndividualSignupForm from "./individual/IndividualSignupForm";
import {
  createErrorSelector,
  createLoadingSelector,
} from "../../store/selectors";
import { userConstants } from "../../actions/types";
import { userEmail } from "../../store/selectors/user";
import Alert from "components/Alert";
import type { RootState } from "types/state";

const authActions = [
  userConstants.CREATE_AND_LINK_SECONDARY_AUTH,
  userConstants.LINK_SECONDARY_AUTH,
  userConstants.CREATE_AUTH_LINKING_SESSION,
];

const isFetchingSelector = createLoadingSelector(authActions);
const errorSelector = createErrorSelector(authActions);
const isLinkingSSOSelector = createLoadingSelector(
  userConstants.LINK_SECONDARY_AUTH
);

interface OwnProps {}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const { user } = state;

  return {
    user,
    ownProps,
    isLinkingSSO: isLinkingSSOSelector(state),
    isFetching: isFetchingSelector(state),
    error: errorSelector(state),
    userEmail: userEmail(state),
  };
};

const mapDispatchToProps = {
  linkSecondaryAuth,
  createAndLinkSecondaryAuth,
  createAuthLinkingSession,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

interface State {
  showIconAccount: boolean;
  isComplete: boolean;
  auth0Error?: unknown;
}

class LinkSecondaryAuth extends React.Component<Props, State> {
  static contextType = Auth0Context;
  // static contextType alone doesn't type this.context — declare it from the
  // (now-typed) Auth0Context so this.context is Auth0ContextValue | undefined.
  declare context: React.ContextType<typeof Auth0Context>;

  constructor(props: Props) {
    super(props);
    this.state = {
      showIconAccount: false,
      isComplete: false,
    };
  }

  linkAccount = async () => {
    try {
      const authLinkingSession = await this.props.createAuthLinkingSession(
        this.props.client as ApolloClient<object>
      );

      const authSessionId = get(
        authLinkingSession,
        "data.createAuthLinkingSession.id"
      );

      if (!this.props.error) {
        this.setState({ isComplete: true });
        return this.context!.loginWithRedirect({
          authorizationParams: {
            scope: "openid email profile",
            connection: "google-oauth2",
          },
          appState: {
            authLinkingSessionId: authSessionId,
            targetUrl: "/",
          },
        });
      }
    } catch (e) {
      this.setState({ auth0Error: e });
    }
  };

  createAndLinkAccount = async (user: {
    email: string;
    password: string;
    username: string;
  }) => {
    const { email, password, username } = user;
    void this.props
      .createAndLinkSecondaryAuth(this.props.client as ApolloClient<object>, {
        email,
        password,
        username,
      })
      .then(() => {
        if (!this.props.error) {
          this.setState({ isComplete: true });
          window.location.reload();
        }
      });
  };

  render() {
    // when we are complete we are going to reload the page which takes a second or two so should still display async status
    const isFetching = this.props.isFetching || this.state.isComplete;
    return (
      <div>
        {isFetching && <IconSpinner centered />}
        {!isFetching && (
          <>
            <section style={{ padding: 0 }}>
              <article>
                <Button
                  name={"submit"}
                  btnLabel={"Continue with Google"}
                  onClick={this.linkAccount}
                  // @ts-expect-error Button's color union omits "google"; preserved runtime value (renders button-google class)
                  color="google"
                  type={"button"}
                  icon={{
                    icon: <FcGoogle color="white" stroke="#60A4BF" size={16} />,
                    position: "left",
                  }}
                  loading={this.props.isLinkingSSO}
                />
              </article>

              <article>
                <div className="divider-container">
                  <div className="divider-line"></div>
                  <span className="divider-text">Or</span>
                  <div className="divider-line"></div>
                </div>
              </article>

              {!this.state.showIconAccount && (
                <article style={{ paddingBottom: 10 }}>
                  <Button
                    name={"submit"}
                    btnLabel={"Continue with Icon Account"}
                    onClick={() => this.setState({ showIconAccount: true })}
                    color="create-account"
                    type="button"
                    loading={this.props.isLinkingSSO}
                  />
                </article>
              )}
            </section>

            {this.state.showIconAccount && (
              <IndividualSignupForm
                error={this.props.error}
                isFetching={this.props.isFetching}
                onSubmit={this.createAndLinkAccount}
                invitationCode={""}
                inviteId={""}
                showInvitationCodeForm={false}
                showTerms={false}
                initialValues={{
                  email: this.props.userEmail,
                }}
              />
            )}
          </>
        )}
        {this.props.error && <Alert type="error" msg={this.props.error} />}
      </div>
    );
  }
}

export default connector(withApollo<Props>(LinkSecondaryAuth));
